import { useParams, Link } from 'react-router-dom';
import { featuredArticles, latestArticles, categories } from '@/mocks/gossip';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const allArticles = [...featuredArticles, ...latestArticles];
  const filteredArticles = allArticles.filter((a) => a.categorySlug === slug);
  const otherCategories = categories.filter((c) => c.slug !== slug);

  const seoSchema = category ? {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": `${category.name} - 吃瓜网`,
    "description": category.description,
    "url": `http://lrayneca.cn/category/${category.slug}`,
    "isPartOf": {
      "@type": "WebSite",
      "name": "吃瓜网",
      "url": "http://lrayneca.cn/"
    }
  } : null;

  const breadcrumbSchema = category ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "首页", "item": "http://lrayneca.cn/" },
      { "@type": "ListItem", "position": 2, "name": category.name }
    ]
  } : null;

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Header />
        <main className="pt-16 flex items-center justify-center min-h-[60vh]">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
              <i className="ri-emotion-sad-line text-3xl text-secondary-500"></i>
            </div>
            <h1 className="text-2xl font-bold text-foreground-900 font-heading mb-2">分类未找到</h1>
            <p className="text-foreground-600 mb-6">这个瓜田好像不存在，去看看别的吧</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 font-medium text-sm rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {seoSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(seoSchema) }} />
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      )}
      <Header />

      <main className="pt-16">
        <section className="bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                <i className={`${category.icon} text-xl`}></i>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading">
                {category.name}
              </h1>
            </div>
            <p className="text-foreground-600 ml-[52px]">{category.description}</p>
          </div>
        </section>

        <section className="py-10 md:py-14">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-8">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-6">
                  <p className="text-sm text-foreground-600">
                    共找到 <strong className="text-foreground-900">{filteredArticles.length}</strong> 篇猛料
                  </p>
                </div>

                {filteredArticles.length > 0 ? (
                  <div className="space-y-4">
                    {filteredArticles.map((article, idx) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.slug}`}
                        className={`flex flex-col sm:flex-row gap-4 p-4 md:p-5 rounded-2xl transition-colors cursor-pointer group ${
                          idx === 0
                            ? 'bg-accent-50 hover:bg-accent-100/70'
                            : 'bg-background-50 hover:bg-background-100'
                        }`}
                      >
                        <div className="sm:w-48 md:w-56 flex-shrink-0">
                          <div className="aspect-[16/10] sm:aspect-[4/3] rounded-xl overflow-hidden">
                            <img
                              src={article.imageUrl}
                              alt={article.title}
                              className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                              loading="lazy"
                            />
                          </div>
                        </div>

                        <div className="flex-1 min-w-0 flex flex-col justify-between py-1">
                          <div>
                            <div className="flex items-center gap-2 mb-2 flex-wrap">
                              {article.hot && (
                                <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-[10px] font-bold rounded-full flex items-center gap-0.5">
                                  <i className="ri-fire-line text-[8px]"></i>
                                  爆
                                </span>
                              )}
                              <span className="text-xs text-foreground-500">{article.date}</span>
                            </div>

                            <h3 className="text-base md:text-lg font-bold text-foreground-900 group-hover:text-primary-700 transition-colors line-clamp-2 leading-snug mb-2">
                              {article.title}
                            </h3>

                            <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">
                              {article.excerpt}
                            </p>
                          </div>

                          <div className="flex items-center gap-3 mt-3 text-xs text-foreground-500">
                            <span className="flex items-center gap-1">
                              <i className="ri-eye-line"></i>
                              {article.views > 10000
                                ? `${(article.views / 10000).toFixed(1)}万`
                                : article.views}
                            </span>
                            <div className="flex flex-wrap gap-1.5">
                              {article.tags.slice(0, 3).map((tag) => (
                                <span
                                  key={tag}
                                  className="px-2 py-0.5 bg-secondary-100 text-secondary-600 text-[10px] rounded-full"
                                >
                                  #{tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-secondary-100">
                      <i className="ri-inbox-line text-2xl text-secondary-500"></i>
                    </div>
                    <p className="text-foreground-600">这个分类下暂时没有猛料，换个瓜田逛逛吧</p>
                  </div>
                )}
              </div>

              <aside className="lg:w-64 flex-shrink-0">
                <div className="sticky top-24">
                  <h3 className="text-sm font-semibold text-foreground-900 mb-4">其他分类</h3>
                  <div className="space-y-2">
                    {otherCategories.map((cat) => (
                      <Link
                        key={cat.slug}
                        to={`/category/${cat.slug}`}
                        className="flex items-center gap-3 px-4 py-3 rounded-xl bg-background-100 hover:bg-background-200/70 transition-colors cursor-pointer group"
                      >
                        <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 group-hover:bg-primary-100 group-hover:text-primary-600 transition-colors">
                          <i className={`${cat.icon} text-sm`}></i>
                        </div>
                        <span className="text-sm font-medium text-foreground-700 group-hover:text-foreground-900 transition-colors whitespace-nowrap">
                          {cat.name}
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
